import { useState } from "react";
import { ConfirmDialog, TrashIcon } from "@otto/ui";
import type { Code } from "../lib/data.js";
import own from "./CodeTable.module.css";

/**
 * Die Codes einer Klasse – so viele wie möglich ohne Scrollen.
 *
 * Eine Zeile je Code war eine Spalte Text und daneben ein Knopf: bei
 * fünfundzwanzig Kindern eine Bildschirmhöhe für Information, die in ein Drittel
 * passt. Jetzt stehen sie in Spalten nebeneinander, so viele, wie die Breite
 * hergibt – auf einem großen Monitor sieht die Lehrkraft den ganzen Klassensatz
 * auf einmal.
 *
 * Bewusst kein `<table>`: die Spaltenzahl soll sich der Breite anpassen. Eine
 * Tabelle mit fester Spaltenzahl zeigt auf dem großen Monitor zu wenige und
 * scrollt auf dem Laptop quer.
 *
 * Löschen geht nur bei Codes, unter denen noch nicht geübt wurde. Der
 * Fortschritt hinge sonst an einer Kaskade, die ein Fehlklick auslöst.
 *
 * Gefragt wird in einem Dialog, nicht unter der Liste. Vorher schob sich die
 * Rückfrage in die Seite und verschob alles darunter – bei einem Klassensatz
 * springt damit genau das weg, worauf man gerade zeigt.
 */
export function CodeTable({
  codes,
  onDelete,
}: {
  codes: readonly Code[];
  onDelete: (id: string) => void;
}) {
  const [toDelete, setToDelete] = useState<string | null>(null);
  const chosen = codes.find((entry) => entry.id === toDelete) ?? null;

  return (
    <>
      <ul className={own.grid}>
        {codes.map((entry) => (
          <li key={entry.id} className={own.item}>
            <code className={own.code}>{entry.code}</code>
            {entry.used ? (
              // Kein Knopf, sondern ein Zeichen: hier wurde geübt. Ein
              // ausgegrauter Knopf ließe die Lehrkraft raten, warum er nicht
              // geht.
              <span
                className={own.used}
                title="Unter diesem Code wurde schon geübt – er lässt sich nicht löschen."
                aria-label="schon geübt"
              >
                ✓
              </span>
            ) : (
              <button
                type="button"
                className={own.remove}
                title={`${entry.code} löschen`}
                aria-label={`${entry.code} löschen`}
                onClick={() => setToDelete(entry.id)}
              >
                <TrashIcon />
              </button>
            )}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={chosen !== null}
        title="Übungscode löschen?"
        confirmLabel="Löschen"
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          if (!chosen) return;
          onDelete(chosen.id);
          setToDelete(null);
        }}
      >
        <code>{chosen?.code}</code> – das Kärtchen wird damit ungültig.
      </ConfirmDialog>
    </>
  );
}
